const fmt = (n) => (n ?? 0).toLocaleString('en-IN')
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'

export function buildInvoicePDF(invoice, student, org, newPayment, logoBase64 = null) {
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const orgAddress = [org.city, org.state, org.pincode ? String(org.pincode) : ''].filter(Boolean).join(', ')
  const studentAddress = [student.address, student.city, student.state, student.pincode ? String(student.pincode) : ''].filter(Boolean).join(', ')

  let runningBalance = invoice.totalAmount
  const historyRows = (invoice.payments || []).map((p, i) => {
    runningBalance -= p.amountReceived
    return { ...p, index: i + 1, runningBalance }
  })
  const newRunningBalance = runningBalance - newPayment.amountReceived
  const hasPrevPayments = historyRows.length > 0

  const orgTextStack = [
    { text: (org.name || '').toUpperCase(), style: 'orgName' },
    org.license ? { text: `License No: ${org.license}`, style: 'orgMeta' } : { text: '' },
    org.address ? { text: org.address, style: 'orgMeta' } : { text: '' },
    orgAddress ? { text: orgAddress, style: 'orgMeta' } : { text: '' },
    org.phoneNumber ? { text: `Ph: ${org.phoneNumber}`, style: 'orgMeta' } : { text: '' },
  ]

  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [45, 45, 45, 50],
    info: { title: `Invoice-${student.name}-${today}` },
    content: [
      // HEADER — org info left, logo right
      {
        columns: [
          { width: '*', stack: orgTextStack },
          ...(logoBase64
            ? [{ width: 100, stack: [{ image: logoBase64, width: 90, height: 90, alignment: 'right' }] }]
            : []),
        ],
        columnGap: 12,
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 505, y2: 0, lineWidth: 2.5, lineColor: '#1565C0' }], margin: [0, 14, 0, 16] },

      // BILL TO + INVOICE TITLE
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: 'BILL TO', style: 'sectionLabel' },
              { text: student.name || '', style: 'studentName' },
              student.phoneNumber ? { text: `Phone: ${student.phoneNumber}`, style: 'bodySmall' } : { text: '' },
              student.guardianName ? { text: `Guardian: ${student.guardianName}`, style: 'bodySmall' } : { text: '' },
              studentAddress ? { text: studentAddress, style: 'bodySmall' } : { text: '' },
            ],
          },
          {
            width: 'auto',
            alignment: 'right',
            stack: [
              { text: 'INVOICE', style: 'invoiceTitle' },
              { text: `Date: ${today}`, style: 'invoiceMeta' },
            ],
          },
        ],
      },

      // PAYMENT SUMMARY
      { text: '', margin: [0, 18, 0, 0] },
      { text: 'PAYMENT SUMMARY', style: 'sectionLabel', margin: [0, 0, 0, 6] },
      {
        table: {
          headerRows: 0,
          widths: ['*', 130],
          body: [
            ...(invoice.originalAmount && invoice.discount > 0
              ? [
                  [
                    { text: 'Standard Course Fee', style: 'summaryKey', color: '#888888' },
                    { text: `Rs. ${fmt(invoice.originalAmount)}`, style: 'summaryVal', alignment: 'right', color: '#888888', decoration: 'lineThrough' },
                  ],
                  [
                    { text: 'Discount', style: 'summaryKey', color: '#C62828' },
                    { text: `- Rs. ${fmt(invoice.discount)}`, style: 'summaryVal', alignment: 'right', color: '#C62828' },
                  ],
                ]
              : []),
            [
              { text: invoice.originalAmount && invoice.discount > 0 ? 'Total After Discount' : 'Total Course Fee', style: 'summaryKey' },
              { text: `Rs. ${fmt(invoice.totalAmount)}`, style: 'summaryVal', alignment: 'right' },
            ],
            ...(hasPrevPayments
              ? [[
                  { text: 'Previously Paid', style: 'summaryKey', color: '#444' },
                  { text: `Rs. ${fmt(invoice.totalPaid)}`, style: 'summaryVal', alignment: 'right', color: '#2E7D32' },
                ]]
              : []),
            [
              { text: 'Amount Received (This Payment)', style: 'summaryKey' },
              { text: `Rs. ${fmt(newPayment.amountReceived)}`, style: 'summaryVal', alignment: 'right', color: '#2E7D32' },
            ],
            [
              {
                text: newRunningBalance <= 0 ? 'Fully Paid' : 'Balance Due',
                style: 'summaryKeyBold',
                fillColor: newRunningBalance <= 0 ? '#E8F5E9' : '#FFF8E1',
              },
              {
                text: `Rs. ${fmt(newRunningBalance)}`,
                style: 'summaryValBold',
                alignment: 'right',
                color: newRunningBalance <= 0 ? '#1B5E20' : '#C62828',
                fillColor: newRunningBalance <= 0 ? '#E8F5E9' : '#FFF8E1',
              },
            ],
          ],
        },
        layout: {
          hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 1 : 0.5,
          vLineWidth: () => 0,
          hLineColor: () => '#DDDDDD',
          paddingLeft: () => 10, paddingRight: () => 10,
          paddingTop: () => 9, paddingBottom: () => 9,
        },
      },

      // TRANSACTION HISTORY
      ...(hasPrevPayments
        ? [
            { text: '', margin: [0, 22, 0, 0] },
            { text: 'TRANSACTION HISTORY', style: 'sectionLabel', margin: [0, 0, 0, 6] },
            {
              table: {
                headerRows: 1,
                widths: [22, '*', 80, 90, 60],
                body: [
                  [
                    { text: '#', style: 'txnHead', fillColor: '#1565C0', color: '#FFF' },
                    { text: 'Date', style: 'txnHead', fillColor: '#1565C0', color: '#FFF' },
                    { text: 'Paid (Rs.)', style: 'txnHead', fillColor: '#1565C0', color: '#FFF', alignment: 'right' },
                    { text: 'Balance (Rs.)', style: 'txnHead', fillColor: '#1565C0', color: '#FFF', alignment: 'right' },
                    { text: 'Mode', style: 'txnHead', fillColor: '#1565C0', color: '#FFF', alignment: 'center' },
                  ],
                  ...historyRows.map((row, i) => {
                    const bg = i % 2 === 0 ? '#FFFFFF' : '#F5F5F5'
                    return [
                      { text: String(row.index), style: 'txnCell', fillColor: bg },
                      { text: fmtDate(row.createdAt), style: 'txnCell', fillColor: bg },
                      { text: fmt(row.amountReceived), style: 'txnCell', alignment: 'right', fillColor: bg, color: '#2E7D32' },
                      { text: fmt(row.runningBalance), style: 'txnCell', alignment: 'right', fillColor: bg, color: row.runningBalance > 0 ? '#C62828' : '#1B5E20' },
                      { text: row.paymentMode || '—', style: 'txnCell', alignment: 'center', fillColor: bg },
                    ]
                  }),
                ],
              },
              layout: {
                hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 1 : 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => '#E0E0E0',
                vLineColor: () => '#EEEEEE',
                paddingLeft: () => 8, paddingRight: () => 8,
                paddingTop: () => 6, paddingBottom: () => 6,
              },
            },
          ]
        : []),

      // SIGNATURE
      {
        columns: [
          { width: '*', text: '' },
          {
            width: 175,
            alignment: 'center',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 175, y2: 0, lineWidth: 1, lineColor: '#AAAAAA' }], margin: [0, 38, 0, 5] },
              { text: 'Authorized Signatory', style: 'sigLabel' },
              org.proprietorName
                ? {
                    text: org.proprietorName
                      .split(' ')
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                      .join(' '),
                    style: 'sigName',
                  }
                : { text: '' },
            ],
          },
        ],
        margin: [0, 28, 0, 0],
      },

      // TERMS AND CONDITIONS
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 505, y2: 0, lineWidth: 0.5, lineColor: '#DDDDDD' }], margin: [0, 18, 0, 10] },
      { text: 'TERMS AND CONDITIONS', fontSize: 9, bold: true, color: '#1565C0', decoration: 'underline', margin: [0, 0, 0, 6] },
      {
        text: 'NOTE - DRIVING CLASSES SHOULD BE COMPLETED WITHIN 45 DAYS OR MENTIONED DATE BY DRIVING SCHOOL FROM THE DATE OF ADMISSION',
        fontSize: 8,
        bold: true,
        color: '#C62828',
        alignment: 'center',
        margin: [0, 0, 0, 10],
      },
      {
        columns: [
          {
            width: '50%',
            ol: [
              "Carry valid Learner's Licence during training.",
              'Be present on time for classes.',
              'Follow instructor instructions carefully.',
              'Seat belt/helmet is compulsory.',
              'Mobile phone use while driving is prohibited.',
              'Rash driving or unsafe behavior is not allowed.',
              'Alcohol/intoxication before training is strictly prohibited.',
            ],
            fontSize: 7.5,
            color: '#444444',
            markerColor: '#444444',
          },
          {
            width: '50%',
            ol: [
              'Students must follow all traffic rules and signals.',
              'Licence approval depends on RTO test and rules.',
              'Fees once paid are non-refundable and non-transferable.',
              'Driving course/training fees cannot be transferred to another person.',
              'Any damage caused due to negligence may be chargeable.',
              'Valid documents must be submitted before training.',
              'Training vehicles may be GPS monitored for safety.',
            ],
            start: 8,
            fontSize: 7.5,
            color: '#444444',
            markerColor: '#444444',
          },
        ],
        columnGap: 16,
        margin: [0, 0, 0, 14],
      },

      // FOOTER
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 505, y2: 0, lineWidth: 0.5, lineColor: '#DDDDDD' }], margin: [0, 0, 0, 7] },
      { text: 'This is a computer-generated invoice. Powered by Drivolo.', style: 'footerNote' },
    ],

    styles: {
      orgName:        { fontSize: 15, bold: true, color: '#1A1A1A', margin: [0, 0, 0, 3] },
      orgMeta:        { fontSize: 8.5, color: '#666666', margin: [0, 1, 0, 1] },
      invoiceTitle:   { fontSize: 22, bold: true, color: '#1565C0', margin: [0, 0, 0, 4] },
      invoiceMeta:    { fontSize: 9, color: '#888888' },
      sectionLabel:   { fontSize: 8, bold: true, color: '#1565C0', characterSpacing: 1 },
      studentName:    { fontSize: 13, bold: true, color: '#1A1A1A', margin: [0, 4, 0, 3] },
      bodySmall:      { fontSize: 9, color: '#555555', margin: [0, 1, 0, 1] },
      infoKey:        { fontSize: 9, color: '#888888' },
      infoVal:        { fontSize: 9, bold: true, color: '#1A1A1A' },
      summaryKey:     { fontSize: 10, color: '#333333' },
      summaryVal:     { fontSize: 10, color: '#333333' },
      summaryKeyBold: { fontSize: 11, bold: true, color: '#1A1A1A' },
      summaryValBold: { fontSize: 11, bold: true },
      txnHead:        { fontSize: 9, bold: true },
      txnCell:        { fontSize: 9, color: '#333333' },
      sigLabel:       { fontSize: 8.5, color: '#888888', alignment: 'center' },
      sigName:        { fontSize: 10, bold: true, color: '#1A1A1A', alignment: 'center', margin: [0, 2, 0, 0] },
      footerNote:     { fontSize: 7.5, color: '#AAAAAA', alignment: 'center', italics: true },
      tcItem:         { fontSize: 7.5, color: '#444444', margin: [0, 2, 0, 2] },
    },
  }
}
